import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import type { PaperProps } from '@mui/material/Paper';
import type { PopoverProps } from '@mui/material/Popover';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import { Check, MagnifyingGlass } from '@phosphor-icons/react';
import type { KeyboardEvent, SyntheticEvent } from 'react';
import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  intent,
  radius,
  shadowTokens,
  sizes,
  spacingTokens,
  state,
  surface,
  text,
  typography,
} from '../../theme/tokens/index.ts';
import { Checkbox } from '../Checkbox/index.ts';
import { MenuItem, MenuList } from '../Menu/index.ts';
import { Skeleton } from '../Skeleton/index.ts';
import { InputAdornment, OutlinedInput } from '../TextField/index.ts';
import type { FilterOption } from './FilterDropdownButton.tsx';

// Panel dimensions from Figma (node 1509:1751): 300 wide, 440 max height. These are layout
// params of this specific panel, not design tokens (no width/height token covers them). tokens-ok
const PANEL_WIDTH = 300;
const PANEL_MAX_HEIGHT = 440;

// The Popover surface styling is applied via `slotProps.paper` (below) onto the DEFAULT Popover
// Paper — NOT a swapped-in `slots.paper` component. Swapping the paper component broke Popover's
// internal positioning ref (panel rendered at 0,0 / top-left); styling the default paper keeps
// MUI's positioning intact. Global MuiPaper is untouched (slot-scoped). White fill + 1px neutral
// border + rd-4 + overlay elevation (shadowTokens.elevation8) match the Menu surface family.
//
// The 1px border was code-only until 2026-08-27 (Figma had elevation alone); flagged rather than
// removed, and the designer then added the outline in Figma — bound to `Intent/Neutral/Border` +
// `bd-1`, i.e. exactly this. Nothing to change here; noted so nobody "fixes" it back out.
const paperSx = {
  display: 'flex',
  flexDirection: 'column',
  width: PANEL_WIDTH,
  maxHeight: PANEL_MAX_HEIGHT,
  overflow: 'hidden',
  backgroundColor: surface.white,
  border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
  borderRadius: `${radius.sm}px`,
  // Overlay elevation (Figma elevation/8) — matches Menu/Autocomplete dropdowns.
  boxShadow: shadowTokens.elevation8,
} as const;

const StyledSearchContainer = styled('div')({
  borderBottom: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
  padding: `${spacingTokens.sp8}px ${spacingTokens.sp12}px`,
});

// The scrollable middle region — grows to fill the panel and scrolls when options overflow.
const StyledListRegion = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
});

const StyledFooter = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: spacingTokens.sp8,
  borderTop: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
  // sp-12 horizontal (was sp-16) — Figma 1509:1714, designer 2026-08-27. Now equal to
  // StyledSearchContainer's, so the panel's top and bottom bars line up.
  padding: `${spacingTokens.sp8}px ${spacingTokens.sp12}px`,
});

// Five rows, deliberately ragged — a loading list should look like a list. Percentages, not
// px: the panel's width is a layout param and these follow it. tokens-ok
const SKELETON_ROWS = ['72%', '54%', '81%', '46%', '66%'] as const;

const StyledSkeletonRows = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: spacingTokens.sp12,
  padding: `${spacingTokens.sp12}px ${spacingTokens.sp16}px`,
});

const StyledNoResults = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacingTokens.sp16,
  color: text.secondary,
  ...typography.body2,
});

/**
 * Props for a filter surface's `Paper`.
 *
 * `ref` is omitted — each surface manages its own paper ref, and Popover's positioning depends
 * on it. The `data-*` index signature is deliberate: MUI's own slot-prop types allow data
 * attributes, and narrowing to `PaperProps` alone silently took that away — a consumer hanging
 * an end-to-end test hook on the surface is one of the two things this escape hatch exists for.
 */
export type FilterSurfacePaperProps = Partial<Omit<PaperProps, 'ref'>> & {
  [dataAttribute: `data-${string}`]: unknown;
};

/**
 * An option's label and its optional subline.
 *
 * `ListItemText` rather than a bare text node, because the theme already styles this pair:
 * `MuiMenuItem` gives the primary `body1`, the secondary `captionSm` in `text.secondary`, and
 * makes the secondary follow the label's colour once the row is selected — exactly Figma
 * 3298:5568. Rendering the subline by hand here would restate all of it and then drift.
 *
 * `disableTypography` is NOT used: the theme targets `.MuiListItemText-primary/secondary`.
 */
function OptionText({ option }: { option: FilterOption }) {
  return (
    <ListItemText
      primary={option.label}
      secondary={option.description}
      // The row owns the gap (MenuItem's `gap: sp8`), so the text block adds no margins of its
      // own — otherwise a row with a subline sits differently from one without.
      sx={{ margin: 0 }}
    />
  );
}

export interface FilterDropdownPanelProps {
  /** Id for the Popover surface — referenced by the trigger's `aria-controls`. */
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  options: FilterOption[];
  /**
   * Committed selection — seeds the draft each time the panel opens. In single-select mode this
   * is the applied value as a 0-or-1 length array, so one prop covers both models.
   */
  selected: string[];
  /**
   * Multi-select (checkbox rows, staged draft, Apply/Clear footer) when true — the default and
   * the original behaviour. When **false** the panel is a searchable SINGLE-select list: radio-
   * style rows, no footer, and a pick commits and closes immediately.
   *
   * The two are genuinely different surfaces, not a visual variant. Staging a draft behind an
   * Apply button only earns its keep when there are several independent choices to batch; for one
   * choice it adds a step and invites "apply nothing". The inline single-select `Menu` already
   * commits on click, and this keeps the two consistent — the only thing the panel adds is search.
   */
  multiple?: boolean;
  /** Multi-select only: apply the draft (commit + close). */
  onApply: (value: string[], event: SyntheticEvent) => void;
  /** Multi-select only: clear immediately (commits an empty selection without needing Apply); panel stays open. */
  onClearAll: (event: SyntheticEvent) => void;
  /** Single-select only: a row was picked. Commits and closes — there is no draft to apply. */
  onSelect?: (value: string, event: SyntheticEvent) => void;
  labelledBy?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  applyLabel?: string;
  clearLabel?: string;
  noResultsText?: string;
  /**
   * Options are still in flight. The surface OPENS and shows skeleton rows — it does not stay
   * shut. `disabled` was the only tool before, and a trigger that refuses to open is a worse
   * answer to "wait a moment" than one that opens and says so.
   *
   * A loading list and an empty list must never look alike: "No results" during a pending
   * request is simply untrue, so the skeleton wins over both empty states below.
   */
  loading?: boolean;
  /**
   * Hand the typed query to the consumer instead of filtering `options` locally — the filter's
   * options come from a request. Debounced here (see `searchDebounceMs`), so every consumer does
   * not reinvent a timer at a different interval.
   *
   * Providing this switches OFF local filtering: whatever comes back in `options` is what the
   * list shows. Filtering a server's answer a second time on the client only hides rows the
   * server meant to return.
   */
  onSearchChange?: (query: string) => void;
  /** Debounce before `onSearchChange` fires. Default 300ms. Clearing the field is immediate. */
  searchDebounceMs?: number;
  /** Characters required before a query is issued at all. Default 0 (query on every keystroke). */
  minSearchLength?: number;
  /** Shown while the query is shorter than `minSearchLength` — the FIRST of the two empty states. */
  hintText?: string;
  /** What a screen reader hears while the skeleton shows. Default "Loading options". */
  loadingLabel?: string;
  /**
   * Escape hatch to the surface. `popover` reaches the `Popover` (placement, `container`, a test
   * hook); `paper` reaches the panel surface itself, which is where a width or a `data-testid`
   * goes.
   *
   * What it deliberately cannot reach is the accessibility contract this component owns — the
   * paper's `id`, `role="dialog"` and `aria-labelledby`, and the Popover's `open` / `anchorEl` /
   * `onClose`. Those are applied last and win, so a consumer cannot accidentally detach the
   * trigger's `aria-controls` from the surface it points at.
   *
   * `paper.sx` is MERGED with the panel's own (array form) rather than replacing it, so
   * `sx: { minWidth: 360 }` widens the panel without having to restate its fill, border and
   * radius.
   */
  slotProps?: {
    popover?: Partial<Omit<PopoverProps, 'open' | 'anchorEl' | 'onClose' | 'children'>>;
    paper?: FilterSurfacePaperProps;
  };
}

/**
 * FilterDropdownPanel — the rich dropdown surface for FilterDropdownButton (Figma 1509:1751):
 * search + a scrollable checkbox list + an Apply / Clear All footer, on a `Popover`.
 *
 * Staged selection: ticking updates a local DRAFT; **Apply** commits it and closes; **Clear All**
 * commits an empty selection immediately (stays open); closing any other way (backdrop/Escape)
 * discards the draft. Search filters `options` client-side.
 *
 * Composition — SHARED: `Popover`→`Paper`/`Modal` (styled only via this instance's `paper` slot).
 * Reused: `MenuList` + `MenuItem` + `Checkbox` (rows), `OutlinedInput` + `InputAdornment` (search),
 * `Button` ×2 (footer).
 */
export const FilterDropdownPanel = forwardRef<HTMLDivElement, FilterDropdownPanelProps>(function FilterDropdownPanel(
  {
    id,
    open,
    anchorEl,
    onClose,
    options,
    selected,
    multiple = true,
    onApply,
    onClearAll,
    onSelect,
    labelledBy,
    searchable = true,
    searchPlaceholder = 'Search',
    applyLabel = 'Apply',
    clearLabel = 'Clear Filters',
    noResultsText = 'No results',
    loading = false,
    onSearchChange,
    searchDebounceMs = 300,
    minSearchLength = 0,
    hintText,
    loadingLabel = 'Loading options',
    slotProps,
  }: FilterDropdownPanelProps,
  ref
) {
  const [draft, setDraft] = useState<string[]>(selected);
  const [query, setQuery] = useState('');
  // Vertical placement: open below the trigger by default, flip above when there isn't enough
  // room below and there's more room above.
  const [placeAbove, setPlaceAbove] = useState(false);

  /*
   * ---- server-driven option lists -----------------------------------------------------------
   *
   * When `onSearchChange` is given, the CONSUMER owns filtering and `options` is simply the
   * current page of results. Four consequences, all of them easy to get wrong separately, which
   * is why they live here rather than in each consuming app:
   *
   *  - the query is debounced, so typing "california" is one request and not eleven;
   *  - **clearing the field is immediate**, never debounced — a user who empties a search is
   *    asking to see everything again, and making them wait 300ms for that reads as lag;
   *  - there are TWO empty states, and they mean different things: "type a bit more" and
   *    "nothing matched". Collapsing them into one leaves a user who typed two characters
   *    staring at "No results", which is false;
   *  - **the consumer's query and the field's text are two different things**, and the panel
   *    keeps them in step across close and reopen — see the open effect below.
   */
  const serverDriven = Boolean(onSearchChange);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hold the callback in a ref so the debounce timer never captures a stale closure, and so
  // changing the handler between renders doesn't restart the timer.
  const searchChangeRef = useRef(onSearchChange);
  searchChangeRef.current = onSearchChange;
  // The last query the consumer was actually GIVEN — not what is in the field. The two differ
  // while a debounce is pending and after the field has been reset, and `options` follow this
  // one, so this is what decides whether the consumer has anything stale to retract.
  const lastSentQueryRef = useRef('');

  const cancelPendingSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };
  const sendSearch = (next: string) => {
    lastSentQueryRef.current = next;
    searchChangeRef.current?.(next);
  };

  // Reset the draft (and search) to the committed selection every time the panel opens, so
  // closing without Apply discards in-panel changes. `selected` is snapshotted on open only.
  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot `selected`/`anchorEl` on open only, by design
  useEffect(() => {
    if (!open) return undefined;
    setDraft(selected);
    setQuery('');
    // A server-driven list is the consumer's state, so emptying the field alone would reopen it
    // over the answer to a query that is no longer there: type "grace", close, reopen — a blank
    // search above a list of one Grace (review of PR #22). Hand the consumer the empty query
    // too, and only when it had one: a panel that was never searched must not refetch on every
    // open.
    if (lastSentQueryRef.current !== '') {
      cancelPendingSearch();
      sendSearch('');
    }
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setPlaceAbove(spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow);
    }
    // Closing drops a debounce that has not fired yet. This component stays mounted while the
    // Popover is shut, so an unmount-only cleanup would let a query typed just before closing
    // reach the consumer after the panel is gone — a request for nobody, and `options` left
    // filtered by text no one can see. The same cleanup covers unmounting while open.
    return cancelPendingSearch;
  }, [open]);

  const toggle = (value: string) => {
    setDraft(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  // Clear All: empty the draft (so the checkboxes visibly untick) AND commit immediately.
  const handleClearAll = (event: SyntheticEvent) => {
    setDraft([]);
    onClearAll(event);
  };

  // Apply is disabled when the draft matches the committed selection (nothing to save);
  // Clear Filters is disabled when nothing is selected in the draft.
  const noChanges = draft.length === selected.length && draft.every(v => selected.includes(v));
  const applyDisabled = noChanges;
  const clearDisabled = draft.length === 0;

  const handleQueryChange = (next: string) => {
    setQuery(next);
    if (!serverDriven) return;
    cancelPendingSearch();

    const trimmed = next.trim();
    // Emptying the field, or dropping back below the threshold, resolves immediately — there is
    // no request to spare and the list has to stop showing answers to a query that is gone.
    if (trimmed.length === 0 || trimmed.length < minSearchLength) {
      sendSearch(trimmed);
      return;
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      sendSearch(trimmed);
    }, searchDebounceMs);
  };

  const q = query.trim().toLowerCase();
  const belowThreshold = q.length > 0 && q.length < minSearchLength;
  // Below the threshold there is nothing to show yet — not "no results", which would be a lie
  // about a query that was never issued.
  const showHint = Boolean(hintText) && serverDriven && (belowThreshold || (minSearchLength > 0 && q.length === 0));

  const filtered = useMemo(
    () =>
      // `serverDriven` short-circuits: filtering a server's answer again on the client only
      // hides rows it deliberately returned (a fuzzy or synonym match, say).
      searchable && q && !serverDriven
        ? options.filter(o => (typeof o.label === 'string' ? o.label : o.value).toLowerCase().includes(q))
        : options,
    [options, searchable, q, serverDriven]
  );

  /*
   * ---- Single-select keyboard model ------------------------------------------------------
   *
   * The search field keeps DOM focus the whole time and the arrow keys move a VIRTUAL cursor
   * over the rows (`aria-activedescendant`), rather than moving focus into the list. Anything
   * else breaks typing: if ArrowDown moved real focus to a row, the next character the user
   * types would go nowhere, and searching a list of hundreds — the entire reason this surface
   * exists — would take a Tab back to the field between every keystroke.
   *
   * That combination (a text field that owns focus, a list it drives) is the combobox pattern,
   * which is why the roles below are `combobox` / `listbox` / `option` rather than the
   * `menu` / `menuitemradio` the inline single-select uses. `aria-activedescendant` pointed at a
   * `menuitemradio` inside a `menu` is not a pairing screen readers implement reliably. The
   * constraint that mattered — rows are NOT real radio inputs, so the row is the control and
   * nothing inside it is separately focusable — holds either way.
   *
   * Multi-select is untouched: its rows are real checkboxes in a `group`, Tab reaches each one,
   * and Space toggles natively.
   */
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Open on the applied row (so Enter re-commits what is already applied rather than jumping to
  // the top of the list), and reset to the top whenever the query changes the result set.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read `selected` only when the list changes, by design
  useEffect(() => {
    if (multiple) return;
    const appliedIndex = filtered.findIndex(o => o.value === selected[0]);
    setActiveIndex(appliedIndex >= 0 ? appliedIndex : 0);
    // `selected` is read on purpose only when the list changes — re-running on every commit
    // would fight the user's cursor.
  }, [filtered, multiple, open]);

  // Keep the virtual cursor visible. `block: 'nearest'` scrolls only when the row is actually
  // out of view, so holding ArrowDown doesn't jerk the list around.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `optionId` is derived from a stable `useId`
  useEffect(() => {
    if (multiple || !open) return;
    listRef.current?.querySelector(`#${CSS.escape(optionId(activeIndex))}`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [activeIndex, multiple, open]);

  const selectableIndexes = filtered.map((o, i) => (o.disabled ? -1 : i)).filter(i => i >= 0);

  /** Move the cursor `step` selectable rows, wrapping — disabled rows are skipped, not landed on. */
  const moveActive = (step: 1 | -1) => {
    if (selectableIndexes.length === 0) return;
    const position = selectableIndexes.indexOf(activeIndex);
    const next =
      position === -1
        ? selectableIndexes[step === 1 ? 0 : selectableIndexes.length - 1]
        : selectableIndexes[(position + step + selectableIndexes.length) % selectableIndexes.length];
    setActiveIndex(next);
  };

  const handleSingleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (selectableIndexes.length) setActiveIndex(selectableIndexes[0]);
        break;
      case 'End':
        event.preventDefault();
        if (selectableIndexes.length) setActiveIndex(selectableIndexes[selectableIndexes.length - 1]);
        break;
      case 'Enter': {
        const option = filtered[activeIndex];
        if (!option || option.disabled) return;
        event.preventDefault();
        onSelect?.(option.value, event);
        break;
      }
      // Escape is deliberately NOT handled — it belongs to the Popover, which closes and returns
      // focus to the trigger. Swallowing it here would trap the user in the panel.
      default:
        break;
    }
  };

  // Consumer paper props first, this component's contract last. `sx` is the exception: it is
  // merged as an array (MUI resolves those left-to-right) so a consumer widening the panel keeps
  // its fill, border, radius and elevation instead of having to restate them.
  const { sx: consumerPaperSx, ...consumerPaperRest } = slotProps?.paper ?? {};

  return (
    <Popover
      ref={ref}
      anchorOrigin={{ vertical: placeAbove ? 'top' : 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: placeAbove ? 'bottom' : 'top', horizontal: 'left' }}
      {...slotProps?.popover}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        ...slotProps?.popover?.slotProps,
        paper: {
          ...consumerPaperRest,
          // The trigger announces `aria-haspopup="dialog"` — make the SURFACE the dialog it
          // promises: role + name + the id its `aria-controls` points at (the Popover's Modal
          // root is just positioning chrome). After the consumer spread on purpose: detaching
          // these would break the trigger's `aria-controls`, so they are not overridable.
          id,
          role: 'dialog',
          'aria-labelledby': labelledBy,
          // 4px gap from the trigger. The paper is absolutely positioned, so only marginTop
          // shifts it (marginBottom is ignored): push DOWN when below, UP (negative) when above.
          sx: [
            {
              ...paperSx,
              marginTop: placeAbove ? `-${spacingTokens.sp4}px` : `${spacingTokens.sp4}px`,
            },
            ...(Array.isArray(consumerPaperSx) ? consumerPaperSx : [consumerPaperSx]),
          ],
        },
      }}
    >
      {searchable && (
        <StyledSearchContainer>
          <OutlinedInput
            fullWidth
            autoFocus
            value={query}
            onChange={event => handleQueryChange(event.target.value)}
            placeholder={searchPlaceholder}
            onKeyDown={multiple ? undefined : handleSingleKeyDown}
            inputProps={{
              'aria-label': searchPlaceholder,
              // Single-select: this field IS the combobox that drives the list below. Multi-
              // select leaves it a plain text field — there the rows are real checkboxes that
              // Tab reaches, so there is no virtual cursor to announce.
              ...(multiple
                ? {}
                : {
                    role: 'combobox',
                    'aria-expanded': true,
                    'aria-controls': listId,
                    'aria-autocomplete': 'list',
                    'aria-activedescendant':
                      filtered.length > 0 && filtered[activeIndex] ? optionId(activeIndex) : undefined,
                  }),
            }}
            startAdornment={
              <InputAdornment position="start">
                <MagnifyingGlass size={sizes.height.h16} />
              </InputAdornment>
            }
          />
        </StyledSearchContainer>
      )}

      <StyledListRegion>
        {/*
          Order matters and is the whole of F-08/F-04: LOADING wins over both empty states,
          because "No results" while a request is in flight is a false statement; then the
          "type more characters" hint, because a query that was never issued cannot have
          returned nothing; only then "nothing matched".
        */}
        {loading ? (
          <StyledSkeletonRows aria-busy="true" aria-live="polite" aria-label={loadingLabel}>
            {SKELETON_ROWS.map(row => (
              // Rows of unequal width so the block reads as a list of names rather than as a
              // loading bar. Presentational: the region above carries the announcement, so the
              // screen reader is told "loading" once instead of five times.
              <Skeleton key={row} variant="rounded" width={row} height={sizes.height.h16} />
            ))}
          </StyledSkeletonRows>
        ) : showHint ? (
          <StyledNoResults>{hintText}</StyledNoResults>
        ) : filtered.length === 0 ? (
          <StyledNoResults>{noResultsText}</StyledNoResults>
        ) : (
          <MenuList
            ref={listRef}
            id={listId}
            // Multi: a group of REAL checkboxes (not a listbox/menu) — the input is the
            // accessible, focusable control, Tab reaches it, Space toggles, state is announced
            // natively. The MenuItem row is a pointer-convenience click target only
            // (role="none", tabIndex -1 via MenuList), mirroring the ProductSelector trigger.
            //
            // Single: a `listbox` of `option` rows driven by the search field's virtual cursor
            // (see the keyboard model above). Nothing inside a row is focusable either way.
            role={multiple ? 'group' : 'listbox'}
            aria-label={labelledBy ? undefined : searchPlaceholder}
            aria-labelledby={labelledBy}
            // Without a search field there is no combobox to hold focus, so the list itself
            // becomes the focus holder and carries the cursor.
            tabIndex={!multiple && !searchable ? 0 : undefined}
            autoFocus={!multiple && !searchable}
            onKeyDown={!multiple && !searchable ? handleSingleKeyDown : undefined}
            aria-activedescendant={
              !multiple && !searchable && filtered[activeIndex] ? optionId(activeIndex) : undefined
            }
            // NO vertical-padding override here on purpose. The list keeps the shared MenuList's
            // `sp8`, so the panel stays consistent with every other Menu in the system. A tighter
            // `sp4` was tried on 2026-08-27 and reverted the same day: the designer would rather
            // change it at the source component than let one instance drift.
          >
            {filtered.map((opt, index) => {
              const isSelected = multiple ? draft.includes(opt.value) : selected[0] === opt.value;

              if (!multiple) {
                return (
                  <MenuItem
                    key={opt.value}
                    id={optionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    // `selected` paints the applied row; the virtual cursor is a separate idea
                    // and gets the same hover treatment the pointer would give it, so keyboard
                    // and mouse users see the row the same way.
                    selected={isSelected}
                    disabled={opt.disabled}
                    // Never a tab stop: focus stays in the search field, which is what makes
                    // typing and arrowing work at the same time.
                    tabIndex={-1}
                    sx={index === activeIndex ? { backgroundColor: state.hover } : undefined}
                    onClick={event => onSelect?.(opt.value, event)}
                    // Keep the virtual cursor and the pointer in agreement — moving the mouse
                    // over a row makes it the active one, so a subsequent Enter commits what is
                    // visibly highlighted rather than something further up the list.
                    onMouseMove={() => {
                      if (!opt.disabled && index !== activeIndex) setActiveIndex(index);
                    }}
                  >
                    <OptionText option={opt} />
                    {/* The tick (Figma 3298:6393) is what tells the APPLIED row from the one the
                        cursor is merely on — both are tinted, and without it a keyboard user
                        arrowing past the applied value loses track of which is which. Decorative
                        to a screen reader: `aria-selected` on the row already says it. */}
                    {isSelected && <Check size={sizes.height.h16} weight="bold" aria-hidden />}
                  </MenuItem>
                );
              }

              return (
                <MenuItem
                  key={opt.value}
                  role="none"
                  selected={isSelected}
                  disabled={opt.disabled}
                  onClick={() => toggle(opt.value)}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggle(opt.value)}
                    // A checkbox click also bubbles to the row's onClick — stop it here so one
                    // click doesn't toggle twice.
                    onClick={event => event.stopPropagation()}
                    disabled={opt.disabled}
                    // The visible text is a sibling (not a wrapping <label>), so name the input
                    // directly; ReactNode labels fall back to the option value.
                    slotProps={{
                      input: {
                        'aria-label': typeof opt.label === 'string' ? opt.label : opt.value,
                      },
                    }}
                  />
                  <OptionText option={opt} />
                </MenuItem>
              );
            })}
          </MenuList>
        )}
      </StyledListRegion>

      {multiple && (
        <StyledFooter>
          {/* Order per Figma 1509:1751: low-emphasis Clear Filters first, primary Apply last, so
            the primary CTA sits on the far right (StyledFooter is justify-end). */}
          <Button variant="text" color="error" size="medium" disabled={clearDisabled} onClick={handleClearAll}>
            {clearLabel}
          </Button>
          <Button variant="contained" size="medium" disabled={applyDisabled} onClick={event => onApply(draft, event)}>
            {applyLabel}
          </Button>
        </StyledFooter>
      )}
    </Popover>
  );
});
